/**
 * RiderSelect Component
 * Provides typeahead/dropdown for selecting a rider
 * Fetches riders from API with search functionality
 */

import { useState, useEffect } from 'react';
import Select from 'react-select';

function RiderSelect({ selectedRider, onSelect, disabled, excludeRiders = [] }) {
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch riders from API
   * Called on component mount and when search term changes
   */
  const fetchRiders = async (searchTerm = '') => {
    setLoading(true);
    setError(null);

    try {
      const url = searchTerm
        ? `/api/riders?search=${encodeURIComponent(searchTerm)}`
        : '/api/riders';

      const response = await fetch(url, {
        headers: {
          'X-API-Key': import.meta.env.VITE_API_KEY || ''
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch riders');
      }

      const data = await response.json();
      setRiders(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching riders:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load initial riders on mount
  useEffect(() => {
    fetchRiders();
  }, []);

  /**
   * Handle search input change
   * Debounced search to avoid excessive API calls
   */
  const handleInputChange = (inputValue) => {
    if (inputValue.length >= 2 || inputValue.length === 0) {
      fetchRiders(inputValue);
    }
  };

  /**
   * Transform rider data for react-select
   * Excludes riders already chosen elsewhere (e.g. the primary rider, or another frenemy slot)
   */
  const excludeKeys = new Set(
    excludeRiders.filter(Boolean).map(r => `${r.firstname.toLowerCase()}|${(r.lastname || '').toLowerCase()}`)
  );
  const riderOptions = riders
    .filter(rider => !excludeKeys.has(`${rider.firstname.toLowerCase()}|${(rider.lastname || '').toLowerCase()}`))
    .map(rider => ({
      value: rider,
      label: rider.fullname,
      ...rider
    }));

  const selectedOption = selectedRider
    ? {
        value: selectedRider,
        label: selectedRider.fullname,
        ...selectedRider
      }
    : null;

  return (
    <div className="rider-select">
      <h2>Select Rider</h2>
      <p className="step-description">
        Start by selecting the rider whose performance you want to analyze
      </p>

      {error && (
        <div className="error-message">
          <p>⚠️ {error}</p>
        </div>
      )}

      <Select
        options={riderOptions}
        value={selectedOption}
        onChange={(option) => onSelect(option ? option.value : null)}
        onInputChange={handleInputChange}
        isLoading={loading}
        isDisabled={disabled}
        isClearable
        placeholder="Type to search for a rider..."
        noOptionsMessage={({ inputValue }) =>
          inputValue.length < 2
            ? 'Type at least 2 characters to search'
            : 'No riders found'
        }
        className="rider-select-input"
        classNamePrefix="select"
        styles={{
          control: (base) => ({
            ...base,
            minHeight: '50px',
            fontSize: '16px'
          }),
          menu: (base) => ({
            ...base,
            zIndex: 9999
          })
        }}
      />

      {riders.length > 0 && !selectedRider && (
        <p className="hint-text">
          💡 Showing {riders.length} rider{riders.length !== 1 ? 's' : ''}. Type to narrow down results.
        </p>
      )}
    </div>
  );
}

export default RiderSelect;
